import type { TimeDay } from "../api/types";
import { DAY_END_MIN, DAY_START_MIN } from "./timelineSlots";

/**
 * Board hebdomadaire : la semaine entière sur un seul axe horaire, une ligne par
 * jour. Là où la vue jour sert à éditer un pointage, celle-ci sert à lire une
 * semaine d'un coup d'œil — où sont les trous, quels jours débordent, comment
 * les activités se répartissent.
 *
 * Aucune durée n'est calculée ici : les minutes, les blocs et les recouvrements
 * viennent du serveur (GET /workers/time-days), le même calcul que la feuille de
 * temps, les compteurs du profil et la vue manager. Ce module les place sur l'axe.
 *
 * La board se lit par projet. Le `code` des blocs, activités et totaux est une
 * clé de regroupement (boardKey) : le projet quand l'activité en porte un, sinon
 * le code d'activité.
 */

/**
 * Clé de regroupement d'une tranche ou d'une part. Le kit range tout son temps
 * sous une nature de travail (dev) et met le projet dans context_refs ; avant la
 * 1.8.0, il le mettait dans le code d'activité. Le libellé passe donc avant l'id :
 * un projet garde sa ligne et sa teinte d'une version du kit à l'autre.
 */
export function boardKey(x: {
  activity_code: string | null;
  project_id?: string;
  project_label?: string;
}): string {
  return x.project_label || x.project_id || (x.activity_code ?? "");
}

export interface BoardBlock {
  startMin: number;
  endMin: number;
  /** Clé de regroupement (boardKey), pas forcément un code d'activité. */
  code: string;
  live: boolean;
  /** Temps saisi à la main, par opposition à un pointage reçu. */
  manual: boolean;
  /** Imputé à un projet PM (project_id présent). */
  assigned: boolean;
  /** activity_start de la tranche, pour la réimputer. */
  eventId?: string;
}

/** Trou entre deux blocs, assez long pour mériter d'être nommé. */
export interface BoardGap {
  startMin: number;
  endMin: number;
  minutes: number;
}

/**
 * Recouvrement : une plage où deux activités distinctes courent en même temps.
 *
 * Ce n'est pas un état normal — personne ne travaille sur deux projets à la même
 * minute — mais c'est un état fréquent : une activité laissée ouverte, un import
 * vendor qui recoupe une saisie manuelle. La board le montre au lieu de le
 * masquer : sans ça, la tranche peinte en dernier recouvre l'autre et la semaine
 * paraît saine, alors qu'elle attribue les mêmes minutes à deux projets.
 *
 * Deux tranches du MÊME code qui se recoupent n'en sont pas un : le travail est
 * bien celui qu'annonce la barre, seul son découpage est mal fermé. Rien à
 * arbitrer, donc rien à signaler — la plage se lit comme continue, ce qu'elle
 * est.
 */
export interface BoardOverlap {
  startMin: number;
  endMin: number;
  minutes: number;
  /** Codes distincts en présence, dans l'ordre où ils démarrent — deux au
   * moins, par construction. */
  codes: string[];
}

/** Un projet dans une journée : sa part déclarée, sa plage, ses blocs. Sert le
 * dépliage d'une ligne en sous-lignes par activité. */
export interface BoardDayActivity {
  code: string;
  minutes: number;
  firstMin: number;
  lastMin: number;
  blocks: BoardBlock[];
}

export interface BoardDay {
  dayKey: string;
  /** Jour sans aucun événement — rendu en ligne vide, pas masqué : un trou dans
   * la semaine est une information, le cacher ferait mentir la board. */
  empty: boolean;
  weekend: boolean;
  blocks: BoardBlock[];
  /** Première et dernière minute couvertes — l'amplitude réelle de la journée. */
  firstMin: number;
  lastMin: number;
  /** Somme des blocs du jour. */
  workedMin: number;
  gaps: BoardGap[];
  /** Plages où deux activités distinctes se superposent. */
  overlaps: BoardOverlap[];
  /** Activités du jour, de la plus longue à la plus courte. */
  activities: BoardDayActivity[];
}

export interface BoardTotal {
  code: string;
  minutes: number;
  color: string;
}

export interface WeekBoard {
  days: BoardDay[];
  /** Amplitude affichée, ajustée aux données du jour le plus large. */
  startMin: number;
  endMin: number;
  ticks: number[];
  totals: BoardTotal[];
}

/** Amplitude minimale : sous 4 h, les blocs d'une journée courte s'étirent au
 * point de suggérer une journée pleine. */
const MIN_SPAN_MIN = 4 * 60;

/** Marge respirante de part et d'autre des données, comme la timeline du kit :
 * un bloc collé au bord se lit comme tronqué. */
const AXIS_PAD_MIN = 30;

/** En deçà, un trou est une respiration entre deux tâches ; au-delà, c'est une
 * pause qui mérite d'être nommée. Seuil repris de la timeline du kit. */
const GAP_LABEL_MIN = 45;

/** Nombre de teintes de la palette (var(--wb-s1)…--wb-s8), au-delà desquelles on
 * retombe sur la teinte neutre « divers ». */
const PALETTE_SIZE = 8;

/**
 * Couleur d'une activité, prise dans une palette fixe plutôt que dérivée en HSL.
 *
 * Le rang dans la semaine ferait un mauvais index : l'activité dominante change
 * d'une semaine à l'autre et toutes les couleurs glisseraient avec elle. On
 * indexe donc sur le code lui-même, qui ne bouge pas — un projet garde sa
 * teinte pour toujours, y compris entre deux écrans.
 */
export function activityColor(code: string): string {
  if (!code) return "var(--wb-divers)";
  let h = 0;
  for (let i = 0; i < code.length; i++) h = (h * 31 + code.charCodeAt(i)) >>> 0;
  return `var(--wb-s${(h % PALETTE_SIZE) + 1})`;
}

/**
 * Énumère les jours d'une période, bornes incluses.
 *
 * Le pas se fait en UTC : ajouter 24 h à une date locale saute ou répète un jour
 * lors d'un changement d'heure, et la semaine du passage à l'heure d'hiver
 * afficherait six ou huit lignes.
 */
export function eachDay(periodStart: string, periodEnd: string): string[] {
  const parse = (s: string) => {
    const parts = s.slice(0, 10).split("-").map(Number);
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n)))
      return NaN;
    const [y, m, d] = parts as [number, number, number];
    return Date.UTC(y, m - 1, d);
  };
  const from = parse(periodStart);
  const to = parse(periodEnd);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return [];

  const out: string[] = [];
  for (let t = from; t <= to; t += 86400000) {
    const d = new Date(t);
    const p = (n: number) => String(n).padStart(2, "0");
    out.push(
      `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`,
    );
    if (out.length > 62) break; // garde-fou : une période ne fait pas deux mois
  }
  return out;
}

/** Un jour de la board, placé sur l'axe à partir du calcul du serveur. Un jour
 * absent est vide. */
export function boardDay(dayKey: string, day: TimeDay | undefined): BoardDay {
  const blocks: BoardBlock[] = (day?.blocks ?? [])
    .map((b) => ({
      startMin: b.start_minute,
      endMin: b.end_minute,
      code: boardKey(b),
      live: b.open,
      manual: b.origin === "manual",
      assigned: !!b.project_id,
      ...(b.start_event_id ? { eventId: b.start_event_id } : {}),
    }))
    .sort((a, b) => a.startMin - b.startMin);

  // Trous entre blocs consécutifs. Le parcours suit l'ordre chronologique et
  // retient la fin la plus tardive vue jusque-là : deux blocs qui se
  // chevauchent ne doivent pas fabriquer une pause négative.
  const gaps: BoardGap[] = [];
  let prevEnd = -1;
  for (const b of blocks) {
    if (prevEnd >= 0 && b.startMin - prevEnd >= GAP_LABEL_MIN) {
      gaps.push({
        startMin: prevEnd,
        endMin: b.startMin,
        minutes: b.startMin - prevEnd,
      });
    }
    prevEnd = Math.max(prevEnd, b.endMin);
  }

  // Deux activités imputées au même projet n'en font qu'une ligne.
  const minutesByKey = new Map<string, number>();
  for (const part of day?.project_lines ?? day?.lines ?? []) {
    if (part.worked_minutes <= 0) continue;
    const key = boardKey(part);
    minutesByKey.set(key, (minutesByKey.get(key) ?? 0) + part.worked_minutes);
  }
  const activities: BoardDayActivity[] = [];
  for (const [code, minutes] of minutesByKey) {
    const own = blocks.filter((b) => b.code === code);
    activities.push({
      code,
      minutes,
      firstMin: own.length ? Math.min(...own.map((b) => b.startMin)) : 0,
      lastMin: own.length ? Math.max(...own.map((b) => b.endMin)) : 0,
      blocks: own,
    });
  }
  activities.sort((a, b) => b.minutes - a.minutes);

  const overlaps: BoardOverlap[] = (day?.overlaps ?? []).map((o) => {
    const startMin = o.start_minute;
    const endMin = o.end_minute;
    return { startMin, endMin, minutes: endMin - startMin, codes: o.codes };
  });

  const dow = new Date(`${dayKey}T12:00:00Z`).getUTCDay();
  return {
    dayKey,
    empty: blocks.length === 0 && (day?.worked_minutes ?? 0) === 0,
    weekend: dow === 0 || dow === 6,
    blocks,
    firstMin: blocks.length ? blocks[0]!.startMin : 0,
    lastMin: blocks.length ? Math.max(...blocks.map((b) => b.endMin)) : 0,
    workedMin: day?.worked_minutes ?? 0,
    gaps,
    overlaps,
    activities,
  };
}

/** Place sur l'axe les jours calculés par le serveur. */
export function buildWeekBoard(
  periodStart: string,
  periodEnd: string,
  daysByKey: Map<string, TimeDay>,
): WeekBoard {
  const days: BoardDay[] = [];
  const byCode = new Map<string, number>();

  let minStart = Number.POSITIVE_INFINITY;
  let maxEnd = Number.NEGATIVE_INFINITY;

  for (const dayKey of eachDay(periodStart, periodEnd)) {
    const day = boardDay(dayKey, daysByKey.get(dayKey));
    days.push(day);
    for (const b of day.blocks) {
      minStart = Math.min(minStart, b.startMin);
      maxEnd = Math.max(maxEnd, b.endMin);
    }
    for (const a of day.activities) {
      byCode.set(a.code, (byCode.get(a.code) ?? 0) + a.minutes);
    }
  }

  // Amplitude : on cadre sur les données plutôt que sur 6h–22h fixes, sinon une
  // semaine de bureau occupe le tiers central de la board et se lit mal. Les
  // données du serveur peuvent sortir de 6h–22h : on ne les rogne plus.
  let startMin = DAY_START_MIN;
  let endMin = DAY_END_MIN;
  if (Number.isFinite(minStart) && Number.isFinite(maxEnd)) {
    startMin = Math.max(0, Math.floor((minStart - AXIS_PAD_MIN) / 60) * 60);
    endMin = Math.min(24 * 60, Math.ceil((maxEnd + AXIS_PAD_MIN) / 60) * 60);
    if (endMin - startMin < MIN_SPAN_MIN) {
      const grow = MIN_SPAN_MIN - (endMin - startMin);
      startMin = Math.max(0, startMin - Math.floor(grow / 2));
      endMin = Math.min(24 * 60, startMin + MIN_SPAN_MIN);
      startMin = Math.max(0, endMin - MIN_SPAN_MIN);
    }
  }

  // Graduations aux heures paires, comme la timeline du kit : une par heure
  // sature la piste dès que la semaine dépasse huit heures d'amplitude.
  const step = endMin - startMin > 6 * 60 ? 120 : 60;
  const ticks: number[] = [];
  for (let m = Math.ceil(startMin / step) * step; m <= endMin; m += step) {
    ticks.push(m);
  }

  const totals: BoardTotal[] = [...byCode.entries()]
    .map(([code, minutes]) => ({ code, minutes, color: activityColor(code) }))
    .sort((a, b) => b.minutes - a.minutes);

  return { days, startMin, endMin, ticks, totals };
}

/** Bornes d'une période, au format YYYY-MM-DD. */
export interface PeriodRange {
  start: string;
  end: string;
}

function ymd(t: number): string {
  const d = new Date(t);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}

/**
 * Décale une clé YYYY-MM-DD de `days` jours.
 *
 * En UTC, comme `eachDay` : parti de minuit UTC, un pas en jours locaux tombe à
 * 23 h UTC la veille au passage à l'heure d'été ou d'hiver, et une navigation de
 * semaine en semaine atterrit un dimanche.
 */
export function shiftDayKey(key: string, days: number): string {
  const [y = 1970, m = 1, d = 1] = key.split("-").map(Number);
  return ymd(Date.UTC(y, m - 1, d + days));
}

/**
 * Période en cours — lundi-dimanche, ou le mois selon le réglage du tenant.
 *
 * Sert de repli quand aucune feuille n'existe encore. Une feuille est une
 * consolidation, déclenchée par un admin ou le cron hebdomadaire : elle n'est
 * pas une condition d'existence des pointages. Sans ce repli, un worker dont la
 * feuille n'a pas été générée ne verrait rien de sa propre semaine, alors que
 * ses time_events sont là.
 *
 * Le jour de référence est local (c'est la semaine de l'utilisateur), le calcul
 * se fait en UTC pour ne pas glisser d'un jour au changement d'heure.
 */
export function currentPeriod(
  periodType: "weekly" | "monthly",
  today: Date = new Date(),
): PeriodRange {
  const y = today.getFullYear();
  const m = today.getMonth();
  const d = today.getDate();

  if (periodType === "monthly") {
    return {
      start: ymd(Date.UTC(y, m, 1)),
      end: ymd(Date.UTC(y, m + 1, 0)),
    };
  }

  const midnight = Date.UTC(y, m, d);
  const fromMonday = (new Date(midnight).getUTCDay() + 6) % 7;
  const monday = midnight - fromMonday * 86400000;
  return { start: ymd(monday), end: ymd(monday + 6 * 86400000) };
}

/** Période entière qui précède `period` : semaine lundi-dimanche ou mois. */
export function previousPeriod(
  periodType: "weekly" | "monthly",
  period: PeriodRange,
): PeriodRange {
  const [y = 1970, m = 1, d = 1] = period.start.split("-").map(Number);
  if (periodType === "monthly") {
    return {
      start: ymd(Date.UTC(y, m - 2, 1)),
      end: ymd(Date.UTC(y, m - 1, 0)),
    };
  }
  const monday = Date.UTC(y, m - 1, d) - 7 * 86400000;
  return { start: ymd(monday), end: ymd(monday + 6 * 86400000) };
}

/**
 * Périodes pas encore consolidées, de la période en cours jusqu'à celle qui suit
 * la feuille la plus récente — de la plus récente à la plus ancienne.
 *
 * Une feuille n'existe qu'après consolidation (cron du dimanche soir ou admin),
 * alors que les pointages sont là dès leur réception. Naviguer de feuille en
 * feuille s'arrêtait donc à la dernière consolidée : la semaine en cours, et
 * celles dont les pointages arrivent après coup (timesheet-kit), restaient hors
 * d'atteinte.
 *
 * `latest` borne la feuille la plus récente (YYYY-MM-DD). Une période qui
 * commence au plus tard à sa fin est couverte par elle, même en partie : la
 * liste s'arrête là. Sans feuille, seule la période en cours est rendue.
 *
 * La période en cours fait exception : seule une feuille qui commence avec elle,
 * ou après, la couvre. Une feuille commencée avant et qui déborde dessus — plage
 * saisie à la main en consolidation, d'un jour ou de plusieurs semaines — ne
 * compte que jusqu'à la veille de la période en cours.
 */
export function pendingPeriods(
  periodType: "weekly" | "monthly",
  latest: PeriodRange | undefined,
  today: Date = new Date(),
): PeriodRange[] {
  const current = currentPeriod(periodType, today);
  const ymdPattern = /^\d{4}-\d{2}-\d{2}$/;
  if (!latest || !ymdPattern.test(latest.start) || !ymdPattern.test(latest.end))
    return [current];
  const beforeCurrent = previousPeriod(periodType, current).end;
  const coveredUntil =
    latest.start < current.start && latest.end > beforeCurrent
      ? beforeCurrent
      : latest.end;
  let period = current;
  const pending: PeriodRange[] = [];
  while (period.start > coveredUntil) {
    pending.push(period);
    period = previousPeriod(periodType, period);
  }
  return pending;
}
